import { MODULE_PAGE_NAME, CREDITOS_MODAL } from '@modules/ai-framework/constants/copy'
import { buildNavRoutes } from '@modules/ai-framework/utils/buildNavRoutes'

// O roteiro da boas-vindas falada (spec-009 [US-2] AC-2.2) — o texto do owner, literal,
// como está no `BRIEF.md` da spec. É CONTRATO: nomeia os módulos, os 4 passos, o período
// de teste e os dois SLAs. Editar pela metade quebra o teste que compara bloco a bloco.
//
// ★ POR QUE AQUI, E NÃO EM `constants/TextChat.js` (onde as strings do módulo vivem): o
// arquivo está sendo editado por outra sessão nesta janela, e um roteiro de 1.500
// caracteres entrando nele é conflito garantido. Quando a poeira baixar, mover este
// arquivo para `constants/` é um `git mv` sem mudança de conteúdo — a fronteira que
// importa (copy fora do `.vue`) já está respeitada.
//
// ★ SEM PLACEHOLDER NA TELA (AC-2.4). O roteiro do owner traz um `[nome da aba / caminho
// no menu]` em aberto; ele NUNCA chega ao consultor. O caminho é DERIVADO do código do
// `ai-framework` — o nome do módulo, o título da aba (de `buildNavRoutes`, a mesma função
// que desenha a nav-bar) e o rótulo da sub-aba. Se alguém renomear a aba, o roteiro
// acompanha; se a aba sumir, `caminhoParaContratar()` lança, e o teste pega.
const SEPARADOR_DE_CAMINHO = ' › '
const ROTA_DA_ABA_DE_CREDITOS = 'aiFrameworkCreditos'
// O destino, para quem quiser navegar em vez de ler o caminho (a integração decide).
export const ROTA_PLANOS = 'aiFrameworkCreditosPlanos'

// Emojis não são pronunciáveis, e desde 18/09/2026 também não são pintados (owner,
// vendo a tela: o 🚀 sai do fechamento). Isso NÃO é editar o roteiro — o `BRIEF.md` é
// contrato e o texto do owner continua aqui, inteiro; quem tira o emoji é a LEITURA,
// tanto a da síntese quanto a da tela.
const EMOJIS = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu

/**
 * O texto sem emoji — a MESMA regra para a fala e para a tela.
 *
 * ★ Uma função só de propósito: duas regras separadas divergiriam no primeiro emoji novo,
 * e aí a tela mostraria um símbolo que a voz não diz (ou o contrário).
 */
export function semEmojis(texto) {
  return String(texto || '')
    .replace(EMOJIS, '')
    .replace(/ {2,}/g, ' ')
}

export function caminhoParaContratar() {
  const aba = buildNavRoutes().find((rota) => rota.name === ROTA_DA_ABA_DE_CREDITOS)
  if (!aba) {
    // Falha ALTA e cedo: um roteiro que manda o consultor a um caminho que não existe é
    // pior do que um roteiro que não sobe.
    throw new Error(
      'boas-vindas-falada: a aba de créditos sumiu de `buildNavRoutes` — o roteiro não tem para onde apontar.'
    )
  }
  return [MODULE_PAGE_NAME, aba.title, CREDITOS_MODAL.ABA_PLANOS].join(SEPARADOR_DE_CAMINHO)
}

// ── Rótulos do player ─────────────────────────────────────────────────────────────────
// Ficam aqui pelo mesmo motivo do roteiro: copy fora do `.vue`.
export const BOAS_VINDAS = {
  ARIA: 'Mensagem de boas-vindas do AAWZ Hub',
  ARIA_RETRATO: 'Retrato de quem dá as boas-vindas',
  OUVIR: 'Ouvir',
  PAUSAR: 'Pausar',
  CONTINUAR_FALA: 'Continuar ouvindo',
  // ★ [18/09/2026, mais tarde no mesmo dia] `AVANCAR` VOLTOU — e agora ele faz
  // alguma coisa: é ele que revela o formulário de perguntas da etapa 1. O
  // owner pediu assim depois de ver as duas coisas juntas na tela: *"só a
  // partir dele que vamos mostrar o componente de perguntas... senão fica
  // muita coisa na cara e não fica no esquema de etapas"*.
  AVANCAR: 'Continuar',
  // ⚠️ Histórico: o rótulo saiu mais cedo em 18/09/2026 junto com o botão — owner:
  // *"retire o continuar que por enquanto não vamos fazer nada"*. Quem fecha a mensagem
  // agora é o fim da fala (ou, se o autoplay for recusado, o próprio texto na tela).
  // ★ [18/09/2026] A mensagem entregue FICA na tela (*"a mensagem de boas vindas saiu, quero
  // que fique"*, owner). Estes dois rótulos são o estado colapsado: o convite para reouvir e
  // a linha que explica por que o bloco continua ali — sem ela, um card que não pede nada no
  // alto da conversa parece sobra de tela, não histórico.
  OUVIR_DE_NOVO: 'Ouvir de novo',
  ENTREGUE: 'Mensagem de boas-vindas. Fica aqui para consulta',
  // O áudio não veio (falhou ou estourou o prazo). O texto já está na tela — o aviso só
  // explica a ausência do som, sem sugerir que a mensagem se perdeu.
  SEM_AUDIO: 'Não foi possível reproduzir o áudio agora. A mensagem está escrita acima.',
  // O browser recusou tocar sem um gesto: não é falha, é política de autoplay. O convite
  // diz o que fazer.
  PRECISA_DE_GESTO: 'Toque em Ouvir para escutar a mensagem.',
}

// ★ A SAUDAÇÃO (AC-2.3). O nome vem da SESSÃO, nunca do modelo, e é o PRIMEIRO nome.
// Sem nome, a saudação sai sem nome — nunca "Olá <nome>", nunca "Olá consultor".
//
// O corte no primeiro nome acontece AQUI e não só no host: o host passa o que a sessão
// tem, e `$currentUser().nome` guarda o nome COMPLETO. Confiar em cada chamador para
// cortar é como "Olá Maria Fernanda Albuquerque," chega à tela numa delas.
export function saudacao(primeiroNome) {
  const nome = String(primeiroNome || '')
    .trim()
    .split(/\s+/)[0]
  return nome ? `Olá ${nome},` : 'Olá,'
}

const CORPO = [
  'Bem-vindo ao AAWZ Hub, a plataforma que reúne todos os módulos do seu negócio num só lugar: no atendimento ao cliente, Consolidador, Asset Allocation, CRM, Compliance e Financial Planning; na gestão do negócio, BI, Comissionamento, Controladoria, Partnership e Desempenho.',
  'Como profissional, você já tem todos esses módulos liberados. Os de empresa abrem com contrato white-label, e aí a plataforma leva a marca da sua empresa.',
  'Para começar bem, siga estes quatro passos:',
  '1. Suba sua base de clientes: positivador, diversificador ou planilha própria. É dela que os agentes partem para analisar oportunidades e fazer simulações macro.',
  '2. Traga o máximo de contexto: relatórios, apresentações, análises e histórico de conversas com o cliente. É o que permite simular casos específicos.',
  '3. Seja específico no pedido: diga o objetivo, o formato que você quer (PDF, Excel ou texto) e para quem é a entrega.',
  '4. Revise e refine na mesma conversa. Se a resposta não vier como você queria, é só pedir o ajuste, sem recomeçar do zero.',
]

// O bloco do período de teste é função porque o caminho é MEDIDO, não escrito.
const PERIODO_DE_TESTE = (caminho) =>
  `Você está no período de teste, com tokens limitados. Para contratar mais capacidade, acesse ${caminho}.`

const SLAS =
  'Ficou com dúvida? Use o botão abaixo do chat: profissionais têm resposta em até 24h úteis; empresas, em até 8h úteis.'

const FECHAMENTO =
  'Bom trabalho! 🚀 Agora fale um pouco sobre você e seu objetivo com a plataforma.'

/**
 * Onde está o caminho de créditos DENTRO de um bloco — para a tela transformá-lo em link.
 *
 * Owner, 18/09/2026: *"coloque o link para a parte de compra de créditos"*. Ler um caminho
 * de menu e ter de percorrê-lo à mão é atrito num momento em que a pessoa já decidiu
 * contratar.
 *
 * ★ Localiza pelo PRÓPRIO caminho derivado (`caminhoParaContratar()`), não por índice de
 * bloco nem por regex sobre a frase: se o texto do bloco mudar, o link continua no lugar
 * certo; se a aba for renomeada, o caminho muda nos dois lugares ao mesmo tempo, porque é
 * a mesma fonte. Devolve `null` para todo bloco que não contém o caminho — que é a
 * maioria, e por isso a tela mapeia uma vez num computed em vez de chamar no template.
 */
export function partesComLink(bloco) {
  const caminho = caminhoParaContratar()
  const inicio = String(bloco || '').indexOf(caminho)
  if (inicio === -1) return null
  return {
    antes: bloco.slice(0, inicio),
    link: caminho,
    depois: bloco.slice(inicio + caminho.length),
  }
}

/**
 * Os blocos do roteiro, na ordem, prontos para a tela.
 *
 * Bloco e não parágrafo corrido porque a LEGENDA acompanha a fala destacando um bloco por
 * vez: sem a quebra não há o que destacar.
 */
export function blocosDoRoteiro(primeiroNome) {
  return [saudacao(primeiroNome), ...CORPO, PERIODO_DE_TESTE(caminhoParaContratar()), SLAS, FECHAMENTO]
}

// O roteiro inteiro como texto exibível (o que o AC-2.6 põe na tela de imediato).
export function textoDoRoteiro(primeiroNome) {
  return blocosDoRoteiro(primeiroNome).join('\n\n')
}

// O que vai para a síntese: o mesmo texto, sem os emojis que o TTS leria em voz alta.
export function textoFaladoDoRoteiro(primeiroNome) {
  return semEmojis(textoDoRoteiro(primeiroNome))
}
